import { supabase } from '../lib/supabase';

export const realtimePaymentAlertService = {
  /**
   * Subscribe to real-time payment transaction changes
   * Alerts admins when new bank transfers are detected
   */
  subscribeToPaymentTransactions(onNewPayment, onPaymentUpdate) {
    const channel = supabase?.channel('payment-transactions-realtime')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'payment_transactions',
          filter: 'payment_method=in.(bsp,kinabank,creditbank)'
        },
        (payload) => {
          // New bank transfer detected
          if (onNewPayment) {
            onNewPayment(payload?.new);
          }
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'payment_transactions',
          filter: 'payment_method=in.(bsp,kinabank,creditbank)'
        },
        (payload) => {
          // Payment status updated
          if (onPaymentUpdate) {
            onPaymentUpdate(payload?.new, payload?.old);
          }
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from payment transaction changes
   */
  unsubscribeFromPaymentTransactions(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Get real-time notification count for pending payments
   */
  async getPendingPaymentCount() {
    try {
      const { count, error } = await supabase?.from('payment_transactions')?.select('*', { count: 'exact', head: true })?.in('payment_method', ['bsp', 'kinabank', 'creditbank'])?.eq('reconciliation_status', 'pending');

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error getting pending payment count:', error);
      return 0;
    }
  },

  /**
   * Play notification sound for new payment
   */
  playNotificationSound() {
    // Create audio context for notification sound
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext?.createOscillator();
    const gainNode = audioContext?.createGain();

    oscillator?.connect(gainNode);
    gainNode?.connect(audioContext?.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode?.gain?.setValueAtTime(0.3, audioContext?.currentTime);
    gainNode?.gain?.exponentialRampToValueAtTime(0.01, audioContext?.currentTime + 0.5);

    oscillator?.start(audioContext?.currentTime);
    oscillator?.stop(audioContext?.currentTime + 0.5);
  },

  /**
   * Show browser notification for new payment
   */
  async showBrowserNotification(payment) {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return;
    }

    if (Notification.permission === 'granted') {
      new Notification('New Bank Transfer Detected', {
        body: `K${payment.amount?.toFixed(2)} from ${payment.payment_method?.toUpperCase()}`,
        icon: '/favicon.ico',
        tag: `payment-${payment.id}`,
        requireInteraction: true
      });
    } else if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        new Notification('New Bank Transfer Detected', {
          body: `K${payment.amount?.toFixed(2)} from ${payment.payment_method?.toUpperCase()}`,
          icon: '/favicon.ico',
          tag: `payment-${payment.id}`,
          requireInteraction: true
        });
      }
    }
  },

  /**
   * Format payment notification message
   */
  formatPaymentNotification(payment) {
    return {
      id: payment?.id,
      title: 'New Bank Transfer',
      message: `K${payment?.amount?.toFixed(2)} received via ${payment?.payment_method?.toUpperCase()}`,
      timestamp: new Date(payment.created_at),
      type: 'payment',
      priority: 'high',
      data: payment
    };
  }
};

export default realtimePaymentAlertService;